import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Button, Modal, Spinner } from "@opendate-app/setlist";
import { SeriesChangeList, SeriesNote } from "./SeriesChangeList";
import { formatDateLong, groupOwnValues, joinOwnValues } from "../../../shared/seriesOwnValues";
import { errorText, toastError, toastInfo } from "../../../shared/toastHelper";

export const QUEUED_RESET_NOTE = "Resetting this date in the background. We'll email you when it's done.";

// Puts one series date's own values back to the series: every one, or the keys given.
const SeriesDateResetModal = ({
  teamId, seriesId, seriesName, calendarEventId, startsAt, keys, timeZone, currency, csrfToken, onClose, onReset,
}) => {
  const [date, setDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // A reset that moved some values but kept others, held so its reasons show before the page refreshes.
  const [partial, setPartial] = useState(null);
  const url = `/teams/${teamId}/event_series/${seriesId}/date_reset`;
  const params = { calendar_event_id: calendarEventId, ...(keys && keys.length > 0 ? { keys } : {}) };
  const headers = { "X-CSRF-TOKEN": csrfToken };

  useEffect(() => {
    let cancelled = false;
    axios.get(`${url}/new.json`, { params })
      .then(({ data }) => {
        if (cancelled) return;
        setDate((data.dates || []).find((entry) => entry.event_id === calendarEventId) || { own_values: [], changes: {} });
      })
      .catch(() => {
        if (cancelled) return;
        toastError("Could not load this date's own values. Please try again.");
        onClose();
      });
    return () => { cancelled = true; };
  }, [calendarEventId, (keys || []).join(",")]);

  const reset = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(`${url}.json`, params, { headers });
      // A reset that swaps a chart runs as a job, so nothing has changed yet.
      if (data.queued_count) {
        toastInfo(QUEUED_RESET_NOTE);
        onReset(data);
        return;
      }
      // A skipped date wasn't reset, so the reason stays here instead of in a
      // toast that the event page's reload would wipe.
      const skipped = data.skipped || [];
      if (skipped.length > 0) {
        setError(skipped.map((skip) => skip.reason).join(" "));
        setSaving(false);
        return;
      }
      if ((data.kept || []).length > 0) {
        setPartial(data);
        setSaving(false);
        return;
      }
      onReset(data);
    } catch (e) {
      // A 409 means another push for the series is running; its message says to retry.
      setError(errorText(e, "Could not reset this date. Please try again."));
      setSaving(false);
    }
  };

  const formatting = { currency, timeZone };
  // The server says why a value it keeps stays the date's own: in the preview,
  // and again once a reset that moved the rest is done.
  const keptReason = (own) => ((date.changes || {})[own.key] || {}).reason
    || ((partial && partial.kept) || []).find((kept) => kept.key === own.key)?.reason;
  // A grouped setting can keep some of its keys and reset the rest, so each
  // part gets its own row and a kept row names only what it keeps.
  const rowsFor = ({ label, values }) => {
    const kept = values.filter(keptReason);
    if (kept.length === 0) return [row(label, values)];
    const keptRow = { key: kept[0].key, label, kept: true, from: "", to: `Stays ${joinOwnValues(kept, "own", formatting)}. ${keptReason(kept[0])}` };
    const resetting = values.filter((own) => !kept.includes(own));
    return resetting.length > 0 ? [keptRow, row(label, resetting)] : [keptRow];
  };
  const row = (label, values) => {
    const own = values[0];
    if (own.kind === "extra_row") {
      const note = partial
        ? `Removed ${own.own} from this date (hidden instead if it had sales).`
        : `Remove ${own.own} from this date (hidden instead if it has sales).`;
      return { key: own.key, label, note };
    }
    return { key: own.key, label, from: joinOwnValues(values, "own", formatting), to: joinOwnValues(values, "series", formatting) };
  };
  const groups = date ? groupOwnValues(date.own_values) : [];
  const rows = groups.flatMap(rowsFor);
  const nothingResets = rows.every((entry) => entry.kept);
  const pricesChange = date && date.has_sales && date.own_values.some((own) => own.kind === "money");
  const when = startsAt || (date && date.starts_at);
  const locked = Boolean(date && date.past);
  const finish = () => onReset(partial);
  const keptSummary = (kept) => {
    const listed = (entry) => groups.some(({ values }) => values.some((own) => own.key === entry.key));
    const count = groups.filter(({ values }) => values.some((own) => kept.some((entry) => entry.key === own.key))).length;
    return [
      ...(count > 0 ? [`${count} ${count === 1 ? "value" : "values"} stayed. See why below.`] : []),
      ...kept.filter((entry) => !listed(entry)).map((entry) => (entry.label ? `${entry.label}: ${entry.reason}` : entry.reason)),
    ].join(" ");
  };

  return (
    <Modal
      open
      onClose={(saving && (() => {})) || (partial ? finish : onClose)}
      icon={false}
      width={520}
      title={`Reset ${when ? formatDateLong(when, timeZone) : "this date"} to the series?`}
      description={`Its own values are cleared and it follows ${seriesName} again, including future series changes.`}
      footer={partial ? <Button variant="primary" onClick={finish}>Done</Button> : (
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={reset} disabled={saving || !date || locked || nothingResets}>
            {saving ? "Resetting…" : "Reset to series"}
          </Button>
        </>
      )}
    >
      {date ? (
        <div data-testid="series-date-reset">
          {error && <Alert color="error" title={error} />}
          {partial && (
            <Alert
              color="warning"
              title="Reset, except for what this date has to keep"
              description={keptSummary(partial.kept)}
            />
          )}
          <SeriesChangeList rows={rows.map(({ kept, ...entry }) => entry)} />
          {pricesChange && !locked && <SeriesNote>Tickets already sold keep the price they paid.</SeriesNote>}
          {locked && <SeriesNote tone="warning">{date.result}</SeriesNote>}
        </div>
      ) : <Spinner />}
    </Modal>
  );
};

SeriesDateResetModal.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  seriesId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  seriesName: PropTypes.string.isRequired,
  calendarEventId: PropTypes.number.isRequired,
  startsAt: PropTypes.string,
  keys: PropTypes.arrayOf(PropTypes.string),
  timeZone: PropTypes.string,
  currency: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default SeriesDateResetModal;
